import * as React from 'react'
import { clsx } from 'clsx'

import { XIcon } from '../icons'

import styles from './Banner.module.css'
import iconSlotStyles from '../../styles/atoms/icon-slot.module.css'
import { UnstyledButton } from '../UnstyledButton'
import { VisuallyHidden } from '../VisuallyHidden'
import { Button } from '../Button'
import type { ButtonProps } from '../Button'
import { useDeprecationWarning } from '../../hooks/useDeprecationWarning'

type BannerProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Visual variant of the banner
   */
  variant?: 'critical' | 'warning' | 'info'
  className?: string
  children: React.ReactNode
  /**
   * @deprecated Use a `Banner.Action` button inside the banner content to handle dismissal instead.
   */
  onDismiss?: () => void
}

type BannerTextProps = React.ComponentProps<'div'> & {
  className?: string
  /**
   * Icon rendered before the text, centered on its first line.
   */
  icon?: React.ReactNode
}

const BannerText = React.forwardRef<HTMLParagraphElement, BannerTextProps>(
  ({ className, icon, children, ...props }, forwardedRef) => {
    if (icon) {
      return (
        <p
          ref={forwardedRef}
          className={clsx(
            styles.text,
            iconSlotStyles.row,
            styles.textWithIcon,
            className,
          )}
          {...props}
        >
          <span className={iconSlotStyles.firstLine}>{icon}</span>
          <span className={iconSlotStyles.label}>{children}</span>
        </p>
      )
    }

    return (
      <p ref={forwardedRef} className={clsx(styles.text, className)} {...props}>
        {children}
      </p>
    )
  },
)

BannerText.displayName = 'Banner.Text'

enum BannerButtonVariants {
  critical = 'critical',
  warning = 'warning',
  info = 'primary',
}

const BannerAction = ({ className, children, ...props }: ButtonProps) => {
  const { variant } = useBanner()

  return (
    <Button
      className={clsx(styles.action, className)}
      size="small"
      variant={props?.variant || BannerButtonVariants[variant]}
      {...props}
    >
      {children}
    </Button>
  )
}

BannerAction.displayName = 'Banner.Action'

const BannerActionLink = ({
  className,
  children,
  ...props
}: ButtonProps & React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const { variant } = useBanner()

  return (
    <Button
      as="a"
      className={clsx(styles.action, className)}
      size="small"
      variant={props?.variant || BannerButtonVariants[variant]}
      {...props}
    >
      {children}
    </Button>
  )
}

BannerActionLink.displayName = 'Banner.ActionLink'

const BannerDismiss = ({ className, children, ...props }: ButtonProps) => {
  return (
    <Button
      className={clsx(styles.dismiss, className)}
      size="small"
      variant="secondary"
      {...props}
    >
      {children}
    </Button>
  )
}

BannerDismiss.displayName = 'Banner.Dismiss'

/**
 * Banner Context
 */
interface BannerContextValue {
  variant: 'critical' | 'warning' | 'info'
}

const BannerContext = React.createContext<BannerContextValue | null>(null)

const useBanner = () => {
  const context = React.useContext(BannerContext)

  if (!context) {
    throw new Error('useBanner must be used within a Banner')
  }

  return context
}

const roleByVariant = {
  critical: 'alert',
  warning: 'status',
  info: 'status',
} as const

const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    { variant = 'info', children, className, onDismiss, ...props }: BannerProps,
    forwardedRef,
  ) => {
    useDeprecationWarning(
      onDismiss !== undefined,
      'Banner: The `onDismiss` prop is deprecated. Use a `Banner.Action` button inside the banner content to handle dismissal instead.',
    )

    return (
      <BannerContext.Provider value={{ variant }}>
        <div
          ref={forwardedRef}
          role={roleByVariant[variant]}
          className={clsx(
            styles.banner,
            variant !== 'info' && styles[variant],
            className,
          )}
          {...props}
        >
          <div className={styles.content}>{children}</div>
          {onDismiss && (
            <UnstyledButton className={styles.close} onClick={onDismiss}>
              <VisuallyHidden>Dismiss</VisuallyHidden>
              <XIcon />
            </UnstyledButton>
          )}
        </div>
      </BannerContext.Provider>
    )
  },
)

Banner.displayName = 'Banner'

const BannerObject = Object.assign(Banner, {
  Text: BannerText,
  Action: BannerAction,
  ActionLink: BannerActionLink,
  Dismiss: BannerDismiss,
})

export { BannerObject as Banner }
export type { BannerProps, BannerTextProps }
